/**
 * Copyright 2025 SweetCode. All rights reserved.
 *
 * OpenAI Conversions API helpers (Pro)
 *
 * The list-shaped user data, the obref browser reference, the oppref click
 * reference and the base payload the Conversions API events carry. The adapter
 * falls back to a base without user data when pmw.getOpenAIBaseData is absent,
 * which is the case on a free build.
 *
 * https://developers.openai.com/ads/measurement-pixel
 * */

(function (pmw, undefined) {

	/**
	 * Map the pixel's singular user fields onto the Conversions API's plural
	 * list fields (emails_sha256, countries, ...). Mirrors
	 * OpenAI_CAPI::normalize_user_data_for_capi() on the PHP side.
	 *
	 * @param {object} user Pixel-shaped user object.
	 * @return {object} Conversions API-shaped user object.
	 */
	pmw.openaiUserDataToCapiShape = (user) => {

		const map = {
			email_sha256       : "emails_sha256",
			phone_number_sha256: "phone_numbers_sha256",
			external_id_sha256 : "external_ids_sha256",
			first_name_sha256  : "first_names_sha256",
			last_name_sha256   : "last_names_sha256",
			country            : "countries",
			region             : "regions",
			city               : "cities",
			postal_code        : "postal_codes",
		};

		const capi = {};

		Object.keys(user || {}).forEach((key) => {
			const value = user[key];
			if (value === undefined || value === null || value === "") return;
			capi[map[key] || key] = map[key] ? [ value ] : value;
		});

		return capi;
	};

	/**
	 * The browser reference the OpenAI SDK stores in its __obref first-party
	 * cookie. The Conversions API takes it unhashed as user.obref so the
	 * server event can be matched to this browser. Only read while the pixel
	 * runs, which is gated on marketing consent.
	 *
	 * @return {string|undefined}
	 */
	pmw.getOpenAIObref = () => {
		const obref = pmw.getCookie("__obref");
		if (!obref) return undefined;

		let decoded = obref;
		try {
			decoded = decodeURIComponent(obref);
		} catch (e) {
			// keep the raw value
		}

		decoded = String(decoded).trim();

		return /^[A-Za-z0-9+\/._~=-]{8,256}$/.test(decoded) ? decoded : undefined;
	};

	/**
	 * User matching data for the Conversions API (the server-side copy of a
	 * browser event): the advanced matching fields in the API's list shape
	 * plus the SDK's obref browser reference.
	 *
	 * @return {object}
	 */
	pmw.getOpenAICapiUserData = () => {

		const user = pmw.openaiUserDataToCapiShape(pmw.getOpenAIUserData());

		const obref = pmw.getOpenAIObref();
		if (obref) user.obref = obref;

		return user;
	};

	/**
	 * Resolve the oppref click reference (ChatGPT click/user identifier).
	 *
	 * Priority (mirrors the PHP side in OpenAI_CAPI):
	 * 1. The __oppref first-party cookie the OpenAI SDK writes on the landing
	 *    page (30 days, parent domain; confirmed by OpenAI support). It is the
	 *    canonical source and survives new tabs and return visits.
	 * 2. Fallback: PMW's own capture of the oppref URL parameter
	 *    (event_listeners.js), because the SDK cookie is not guaranteed to be
	 *    set (OpenAI reports it failing to persist on some sites).
	 *
	 * @return {string|undefined}
	 */
	pmw.getOpenAIOppref = () => {
		return pmw.getCookie("__oppref") || pmw.retrieveData("oppref");
	};

	/**
	 * Base OpenAI event data shared by the browser pixel and the Conversions API.
	 *
	 * The oppref click reference is attached at the top level of the event. The
	 * browser SDK (oaiq) pulls oppref from the URL by itself, but the
	 * Conversions API needs it in the event payload. Both server paths preserve
	 * it: the SSP worker passes unknown top-level fields through, and the WC
	 * path copies it in OpenAI_CAPI::send_event_hit().
	 *
	 * @return {object}
	 */
	pmw.getOpenAIBaseData = () => {
		const data = {
			timestamp_ms: Date.now(),
			source_url  : window.location.href,
			// The server-side copy of the event goes to the Conversions API,
			// which takes the plural list fields, not the pixel's singular ones.
			user        : pmw.getOpenAICapiUserData(),
		};

		const oppref = pmw.getOpenAIOppref();
		if (oppref) data.oppref = oppref;

		return data;
	};

}(window.pmw = window.pmw || {}));
